import { Box, Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { Maybe } from 'graphql/jsutils/Maybe';
import { compact } from 'lodash';
import { useMemo } from 'react';

import { SubtleLabelItem } from '@/components/display/SubtleLabelItem/SubtleLabelItem';
import { DropdownButtonProps } from '@/components/form/baseInputs/DropdownButton/DropdownButton';
import { GroupedHeaderList } from '@/components/lists/GroupedHeaderList/GroupedHeaderList';
import { TableCell, TableCellContext } from '@/components/lists/TableCell';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { DispositiveProvisionsListView } from '@/modules/dispositiveProvisions/DispositiveProvisionsListView/DispositiveProvisionsListView';
import { BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE } from '@/modules/entities/BusinessEntityDetailsSubform/BusinessEntityDetailsSubform.types';
import { HypotheticalTransfersCard } from '@/modules/entities/details/HypotheticalTransfersCard/HypotheticalTransfersCard';
import {
  SummaryContainer,
  SummaryWrapper,
} from '@/modules/trusts/TrustDetails/TrustDetails.utils';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';

import { ClientProfileTabs } from './ClientProfileSummaryPanel.types';
import { getSummaryFields } from './ClientProfileSummaryPanel.utils';
import { ClientProfileSummaryPanel_ClientProfileFragment } from './graphql/ClientProfileSummaryPanel.generated';

interface UseClientProfilePanelsInput {
  summaryFields: ReturnType<typeof getSummaryFields>;
  cp: Maybe<ClientProfileSummaryPanel_ClientProfileFragment>;
  currentNodeValue?: Decimal;
}

type ClientProfilePanel = DropdownButtonProps['items'][number] & {
  value: ClientProfileTabs;
};

export function useClientProfilePanels({
  summaryFields,
  cp,
  currentNodeValue,
}: UseClientProfilePanelsInput): ClientProfilePanel[] {
  const assets = useMemo(() => {
    return [
      {
        heading: 'Direct business ownership',
        items:
          cp?.ownedOwnershipStakes?.map((s) => ({
            id: s.id,
            heading: s.ownedEntity?.subtype.displayName ?? EMPTY_CONTENT_HYPHEN,
            value: formatCurrencyNoDecimals(s.ownedValue),
            percentage: s.ownershipPercentage
              ? `${formatPercent(s.ownershipPercentage, BUSINESS_OWNERSHIP_PERCENTAGE_DECIMAL_SCALE)}%`
              : EMPTY_CONTENT_HYPHEN,
          })) ?? [],
      },
    ];
  }, [cp?.ownedOwnershipStakes]);

  return useMemo(() => {
    if (!cp) {
      return [];
    }

    return compact([
      {
        name: 'Summary',
        value: ClientProfileTabs.Summary,
        component: (
          <SummaryWrapper>
            <SummaryContainer>
              <Box flexGrow={1} mt={2}>
                <Stack spacing={2}>
                  <SubtleLabelItem
                    label="Full legal name"
                    value={summaryFields.legalName}
                  />
                  <SubtleLabelItem
                    label="Location"
                    value={summaryFields.location}
                  />
                  <SubtleLabelItem label="Email" value={summaryFields.email} />
                </Stack>
              </Box>
            </SummaryContainer>
          </SummaryWrapper>
        ),
      },
      {
        name: 'Holdings',
        value: ClientProfileTabs.Assets,
        component: (
          <>
            <GroupedHeaderList<{
              id: string;
              heading: string;
              value: string;
              percentage: string;
            }>
              rowGroups={assets}
              ItemComponent={({ item }) => (
                <TableCell
                  heading={item.heading}
                  RightContent={
                    <Stack spacing={1}>
                      <TableCellContext
                        heading={item.value}
                        subheading={item.percentage}
                      />
                    </Stack>
                  }
                />
              )}
              withDividers
            />
          </>
        ),
      },
      {
        name: 'Dispositions',
        value: ClientProfileTabs.Disposition,
        component: (
          <DispositiveProvisionsListView currentNodeValue={currentNodeValue} />
        ),
      },
      {
        name: 'Hypothetical transfers',
        value: ClientProfileTabs.Transfers,
        component: <HypotheticalTransfersCard individual={cp} />,
      },
    ]);
  }, [
    assets,
    cp,
    currentNodeValue,
    summaryFields.email,
    summaryFields.legalName,
    summaryFields.location,
  ]);
}
